// @greenlight-spec: 28-it-dashboard-ui.md
// @greenlight-spec: 19-design-system.md
import type * as React from 'react';

import { GLDrawer } from '@/components/greenlight/GLDrawer';
import { StatusBadge } from '@/components/greenlight/StatusBadge';
import type { RoleSource, UserListItem } from '@greenlight/shared';

import { UserAvatar } from '@/components/greenlight/UserAvatar';
import { UserInspectorBody } from './UserInspectorBody';

export interface UserInspectorProps {
  user: UserListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful mutation so the parent can update its selectedUser snapshot. */
  onUserUpdated?: (updated: UserListItem) => void;
  /** Override the org's role-management mode; otherwise read from settings. */
  roleSource?: RoleSource;
  /** When true, settings are still loading; the role row shows a skeleton. */
  roleLoading?: boolean;
}

/**
 * Responsive inspector panel for a selected user.
 * Renders a bottom Sheet on mobile and a resizable right panel on desktop via GLDrawer.
 * The title slot carries the user's identity strip (avatar + name + email + status).
 */
export function UserInspector({
  user,
  open,
  onOpenChange,
  onUserUpdated,
  roleSource,
  roleLoading,
}: UserInspectorProps): React.JSX.Element {
  const displayName = user ? (user.display_name ?? user.email) : '';

  const headerNode = user ? (
    <div className="flex items-center gap-3 min-w-0">
      <UserAvatar name={displayName} size={46} deactivated={!user.is_active} />
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[17px] font-bold tracking-tight text-foreground truncate">
            {displayName}
          </span>
          {!user.is_active && <StatusBadge variant="inactive">Deactivated</StatusBadge>}
        </div>
        <div className="mt-0.5 text-[12.5px] text-muted-foreground truncate">{user.email}</div>
      </div>
    </div>
  ) : null;

  return (
    <GLDrawer
      open={open}
      onOpenChange={onOpenChange}
      variant="panel"
      title={headerNode ?? ''}
      srDescription={user ? `Details for ${displayName}` : 'User details'}
      storageKey="gl-users-panel-width"
      // Inspector follows the page's md (768px) collapse so overlay and page
      // switch layouts together — tables behind it collapse to card lists at md.
      mobileBreakpoint={768}
    >
      {user ? (
        <UserInspectorBody
          user={user}
          onUserUpdated={onUserUpdated}
          roleSource={roleSource}
          roleLoading={roleLoading}
        />
      ) : null}
    </GLDrawer>
  );
}
